import type { ArchivedChatSummary, Task } from '@afra/shared'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { faDateTime, faNum } from '../platform/labels'
import { HYPOTHESIS_BADGE, HYPOTHESIS_FA } from './Rail'
import { Transcript } from './Transcript'

const TASK_STATUS_FA: Record<Task['status'], string> = {
  open: 'باز',
  done: 'انجام شد',
  abandoned: 'کنار گذاشته شد',
}

/**
 * «گفت‌وگوهای قبلی» — every archived conversation, and how it went.
 *
 * Archiving never deletes, so this is the whole record: what was said, what
 * the agent suspected and whether it held up, and what the charity agreed to
 * do. Read-only; the live conversation is the only one anyone can add to.
 */
export function HistoryPage() {
  const archive = useQuery({ queryKey: ['chat-archive'], queryFn: api.archivedChats })
  const items = archive.data?.archived ?? []

  return (
    <div className="container stack">
      <div>
        <Link to="/journey" className="btn btn--quiet btn--sm">
          بازگشت به گفت‌وگو
        </Link>
        <h1>گفت‌وگوهای قبلی</h1>
        <p className="meta">
          گفت‌وگوهایی که بایگانی شده‌اند، با هر چه در آن‌ها ثبت شد. چیزی از آن‌ها پاک نشده است.
        </p>
      </div>

      {archive.isLoading && <p className="muted">در حال بارگذاری…</p>}
      {archive.isError && <p className="notice notice--error">فهرست بارگذاری نشد.</p>}
      {archive.data && items.length === 0 && (
        <div className="card">
          <p className="muted">
            هنوز گفت‌وگویی بایگانی نشده. هر وقت در صفحهٔ گفت‌وگو «بایگانی و شروع تازه» را بزنید، گفت‌وگوی قبلی
            اینجا می‌آید.
          </p>
        </div>
      )}

      {items.map((item) => (
        <ArchivedCard key={item.id} item={item} />
      ))}
    </div>
  )
}

function ArchivedCard({ item }: { item: ArchivedChatSummary }) {
  const title = item.challengeFa ?? item.firstSaidFa ?? 'گفت‌وگویی که در آن پیامی فرستاده نشد'
  return (
    <div className="card">
      <div className="meta">
        {faDateTime(item.startedOn)} تا {faDateTime(item.archivedOn)}
        {item.archivedByEmail && (
          <>
            {' '}
            · بایگانی‌شده به دست <span className="ltr">{item.archivedByEmail}</span>
          </>
        )}
      </div>
      <h3 style={{ marginBlock: 'var(--space-2)' }}>{title.length > 160 ? `${title.slice(0, 160)}…` : title}</h3>
      {item.outcomeFa && <p className="meta">نتیجهٔ مطلوب: {item.outcomeFa}</p>}
      <div className="row" style={{ marginBlockStart: 'var(--space-3)' }}>
        <span className="meta">
          <span className="num">{faNum(item.saidCount)}</span> پیام از شما ·{' '}
          <span className="num">{faNum(item.hypothesisCount)}</span> حدس
          {item.confirmedCount > 0 && (
            <>
              {' '}
              (<span className="num">{faNum(item.confirmedCount)}</span> تأییدشده)
            </>
          )}{' '}
          · <span className="num">{faNum(item.taskCount)}</span> کار · <span className="num">{faNum(item.noteCount)}</span>{' '}
          نکته
        </span>
        <Link to={`/journey/history/${item.id}`} className="btn btn--ghost btn--sm" style={{ marginInlineStart: 'auto' }}>
          دیدن گفت‌وگو
        </Link>
      </div>
    </div>
  )
}

/** One archived conversation: the transcript, and beside it what it produced. */
export function ArchivedChatPage() {
  const params = useParams()
  const id = Number(params.id)
  const chat = useQuery({
    queryKey: ['chat-archive', id],
    queryFn: () => api.archivedChat(id),
    enabled: Number.isInteger(id) && id > 0,
  })
  const data = chat.data

  return (
    <div className="container container--wide stack">
      <div>
        <Link to="/journey/history" className="btn btn--quiet btn--sm">
          بازگشت به گفت‌وگوهای قبلی
        </Link>
      </div>

      {chat.isLoading && <p className="muted">در حال بارگذاری…</p>}
      {chat.isError && <p className="notice notice--error">این گفت‌وگو پیدا نشد.</p>}

      {data && (
        <div className="chat-layout">
          <div className="chat">
            <div className="chat__head">
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>گفت‌وگوی بایگانی‌شده</div>
                <div className="meta">
                  {faDateTime(data.startedOn)} تا {faDateTime(data.archivedOn)}
                </div>
              </div>
              <a href={`/api/chat/archive/${data.id}/export`} className="btn btn--quiet btn--sm" download>
                خروجی HTML
              </a>
              <span className="badge badge--stored">فقط خواندنی</span>
            </div>
            <div className="chat__body">
              {data.messages.length === 0 ? (
                <p className="muted">در این گفت‌وگو پیامی نیست.</p>
              ) : (
                <Transcript messages={data.messages} />
              )}
            </div>
          </div>

          <div className="rail">
            <div className="rail__block">
              <div className="rail__label">چالش</div>
              {data.goal.challengeFa ? <div>{data.goal.challengeFa}</div> : <div className="rail__empty">ثبت نشد</div>}
            </div>
            <div className="rail__block">
              <div className="rail__label">نتیجهٔ مطلوب</div>
              {data.goal.outcomeFa ? <div>{data.goal.outcomeFa}</div> : <div className="rail__empty">ثبت نشد</div>}
            </div>

            <div className="rail__block">
              <div className="rail__label">حدس‌ها دربارهٔ علت ریشه‌ای</div>
              {data.hypotheses.length === 0 ? (
                <div className="rail__empty">حدسی ثبت نشد</div>
              ) : (
                data.hypotheses.map((h) => (
                  <div key={h.id} className="note" style={h.status === 'refuted' ? { opacity: 0.6 } : undefined}>
                    <span className="note__dot" />
                    <span>
                      {h.textFa} <span className={`badge ${HYPOTHESIS_BADGE[h.status]}`}>{HYPOTHESIS_FA[h.status]}</span>
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="rail__block">
              <div className="rail__label">نکته‌ها</div>
              {data.noted.length === 0 ? (
                <div className="rail__empty">نکته‌ای ثبت نشد</div>
              ) : (
                data.noted.map((n) => (
                  <div key={n.id} className="note">
                    <span className="note__dot" />
                    <span>{n.textFa}</span>
                  </div>
                ))
              )}
            </div>

            <div className="rail__block">
              <div className="rail__label">کارها</div>
              {data.tasks.length === 0 ? (
                <div className="rail__empty">کاری تعریف نشد</div>
              ) : (
                data.tasks.map((t) => (
                  <div key={t.id} className="note">
                    <span className="note__dot" />
                    <span>
                      {t.actionFa}{' '}
                      <span className={`badge ${t.status === 'done' ? 'badge--good' : 'badge--stored'}`}>
                        {TASK_STATUS_FA[t.status]}
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
